import { create } from "zustand";
import type { OriginalDocument, WorkspaceSession } from "@jdoc/document-model";

/**
 * The workspace store holds the current session metadata and a reference to the
 * immutable original bytes. Bytes are kept out of React state trees on purpose
 * (they are large); components read them through `getOriginal()`.
 */

interface WorkspaceState {
  session: WorkspaceSession | null;
  /** Kept in a ref-like slot, not spread into components. */
  original: OriginalDocument | null;
  fileHandle: FileSystemFileHandle | null;
  error: string | null;
  busy: boolean;

  openDocument: (doc: OriginalDocument, handle?: FileSystemFileHandle | null) => void;
  setFileHandle: (handle: FileSystemFileHandle | null) => void;
  newBlankDocx: () => void;
  closeDocument: () => void;
  markDirty: (dirty: boolean) => void;
  setError: (message: string | null) => void;
  setBusy: (busy: boolean) => void;
}

export const useWorkspace = create<WorkspaceState>((set) => ({
  session: null,
  original: null,
  fileHandle: null,
  error: null,
  busy: false,

  openDocument: (doc, handle = null) =>
    set({
      original: doc,
      fileHandle: handle,
      error: null,
      session: {
        id: doc.id,
        kind: doc.kind,
        displayName: doc.displayName,
        byteLength: doc.byteLength,
        openedAt: Date.now(),
        updatedAt: Date.now(),
        dirty: false,
        recoveryVersion: 0,
        compatibilityWarnings: [],
      },
    }),

  setFileHandle: (handle) => set({ fileHandle: handle }),

  newBlankDocx: () =>
    set({
      original: null,
      fileHandle: null,
      error: null,
      session: {
        id:
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `sess_${Date.now()}`,
        kind: "docx",
        displayName: "Untitled document.docx",
        byteLength: 0,
        openedAt: Date.now(),
        updatedAt: Date.now(),
        dirty: false,
        recoveryVersion: 0,
        compatibilityWarnings: [],
      },
    }),

  closeDocument: () => set({ session: null, original: null, fileHandle: null, error: null }),

  markDirty: (dirty) =>
    set((s) =>
      s.session
        ? { session: { ...s.session, dirty, updatedAt: Date.now() } }
        : {},
    ),

  setError: (message) => set({ error: message }),
  setBusy: (busy) => set({ busy }),
}));

